import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    onDocumentReady(this.#preloadImages);

    // Add global event listeners for mega menu control
    document.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('click', this.#handleClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up global event listeners
    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('click', this.#handleClickOutside);

    // Ensure body scroll is restored
    this.#restoreBodyScroll();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();

    if (this.#state.activeItem) {
      this.#activateHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    this.dataset.overflowExpanded = (event.target.slot !== '').toString();

    this.#deactivate();
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);

    if (!submenu) return;

    // Check if submenu has actual visible content
    const hasVisibleContent = this.#hasVisibleSubmenuContent(submenu);

    // Only prevent body scroll if there's actually visible content to show
    if (hasVisibleContent) {
      this.#preventBodyScroll();
    }

    this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);

    // Ensure positioning is correct after activation
    requestAnimationFrame(() => {
      this.#adjustMegaMenuPosition();
    });
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate(event);
    }
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce((event) => {
    this.dataset.overflowExpanded = 'false';

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item != this.#state.activeItem) return;

    this.#deactivate(item);
  }, DEACTIVATE_DELAY);

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.style.setProperty('--submenu-height', '0px');

    if (!item) return;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    // Only restore body scroll if it was actually prevented
    if (document.body.hasAttribute('data-mega-menu-open')) {
      this.#restoreBodyScroll();
    }

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));

    // Preload collection grid images specifically
    const collectionGridImages = this.querySelectorAll('.mega-menu-collection-card__img[loading="lazy"]');
    collectionGridImages?.forEach((image) => {
      image.removeAttribute('loading');
      // Add intersection observer for better performance
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
              }
              observer.unobserve(img);
            }
          });
        });
        observer.observe(image);
      }
    });
  };

  /**
   * Prevent body scroll when mega menu is open
   */
  #preventBodyScroll = () => {
    if (document.body.hasAttribute('data-mega-menu-open')) return;

    // Store current scroll position
    this.#scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Mark that we actually prevented scroll (for restoration logic)
    this.#scrollPrevented = true;

    // Add class and attributes to prevent scroll
    document.body.setAttribute('data-mega-menu-open', '');

    // Check if we're on mobile
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // Only use position fixed on mobile to prevent scrolling issues
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#scrollPosition}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // Prevent scroll on document element
      document.documentElement.style.overflow = 'hidden';
    }
    // For desktop: Don't manipulate body/html at all to preserve sticky header behavior

    // Ensure mega menu positioning is correct
    // Use a small delay to ensure DOM has updated
    requestAnimationFrame(() => {
      this.#adjustMegaMenuPosition();
    });
  };

  /**
   * Restore body scroll when mega menu is closed
   */
  #restoreBodyScroll = () => {
    if (!document.body.hasAttribute('data-mega-menu-open')) return;

    // Reset mega menu positioning first
    this.#resetMegaMenuPosition();

    // Check if we used position fixed (only on mobile)
    const wasPositionFixed = document.body.style.position === 'fixed';
    const isMobile = window.innerWidth < 768;

    // Remove attributes and styles
    document.body.removeAttribute('data-mega-menu-open');

    // Only clean up styles that were actually set
    if (isMobile) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    // Only restore scroll position if we used position fixed (mobile) and have a stored position
    if (wasPositionFixed && isMobile && this.#scrollPrevented && typeof this.#scrollPosition === 'number') {
      // Use requestAnimationFrame to ensure DOM has updated before scrolling
      requestAnimationFrame(() => {
        window.scrollTo(0, this.#scrollPosition);
        this.#scrollPosition = undefined;
        this.#scrollPrevented = false;
      });
    } else {
      // Reset flags even if we didn't restore scroll position
      this.#scrollPosition = undefined;
      this.#scrollPrevented = false;
    }
  };

  /**
   * Check if submenu has visible content that warrants preventing body scroll
   * @param {HTMLElement} submenu - The submenu element to check
   * @returns {boolean} - True if submenu has visible content
   */
  #hasVisibleSubmenuContent = (submenu) => {
    if (!submenu) return false;

    // Check if submenu has any child elements
    if (submenu.children.length === 0) return false;

    // Check if submenu has actual content (not just empty divs)
    const hasContent = submenu.querySelector('.mega-menu__list, .mega-menu-collection-grid, .menu-list__submenu-inner');
    if (!hasContent) return false;

    // Check if the content has actual menu items or collections
    const hasMenuItems = submenu.querySelector('li, .mega-menu-collection-card, .mega-menu__content-list-item');
    if (!hasMenuItems) return false;

    // Check if submenu would have a meaningful height (more than just padding)
    const submenuHeight = submenu.offsetHeight;
    return submenuHeight > 50; // Minimum height threshold for meaningful content
  };

  /**
   * Stored scroll position for restoration
   * @type {number | undefined}
   */
  #scrollPosition;

  /**
   * Flag to track if scroll was actually prevented
   * @type {boolean}
   */
  #scrollPrevented = false;

  /**
   * Adjust mega menu position when mega menu is open
   */
  #adjustMegaMenuPosition = () => {
    const activeMenus = this.querySelectorAll('.menu-list__submenu[aria-expanded="true"]');
    const header = document.querySelector('#header-component');
    const isMobile = window.innerWidth < 768;

    activeMenus.forEach(menu => {
      // Reset any existing inline styles that might interfere
      menu.style.transform = '';
      menu.style.position = '';
      menu.style.top = '';
      menu.style.left = '';
      menu.style.right = '';
      menu.style.zIndex = '';

      if (isMobile) {
        // Mobile: Use fixed positioning
        const headerHeight = header ? header.offsetHeight : 80;
        menu.style.position = 'fixed';
        menu.style.top = `${headerHeight}px`;
        menu.style.left = '0';
        menu.style.right = '0';
        menu.style.zIndex = 'var(--layer-header-menu, 1000)';
      } else {
        // Desktop: Use absolute positioning to preserve sticky header behavior
        menu.style.position = 'absolute';
        menu.style.top = '100%';
        menu.style.left = '0';
        menu.style.right = '0';
        menu.style.zIndex = 'var(--layer-header-menu, 1000)';
      }
    });
  };

  /**
   * Reset mega menu positioning to default
   */
  #resetMegaMenuPosition = () => {
    const activeMenus = this.querySelectorAll('.menu-list__submenu');

    activeMenus.forEach(menu => {
      // Reset all inline positioning styles to let CSS take over
      menu.style.transform = '';
      menu.style.position = '';
      menu.style.top = '';
      menu.style.left = '';
      menu.style.right = '';
      menu.style.zIndex = '';
    });
  };

  /**
   * Handle keydown events for mega menu control
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    // Close mega menu on Escape key
    if (event.key === 'Escape' && this.#state.activeItem) {
      event.preventDefault();
      this.#deactivate();
    }
  };

  /**
   * Handle click outside mega menu to close it
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    if (!this.#state.activeItem) return;

    // Check if click is outside the header menu
    if (!this.contains(event.target)) {
      this.#deactivate();
    }
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[role="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
